/**
 * Pure part of the teacher contract status (no API / Vue imports, offline check tests/contracts/teacherContracts.check.ts):
 * texts, cache + batching loader, watch keys.
 */
import type {EmployeeContractStatus} from '@/classes/api/contract-manager.api.class';

export const MISSING_CONTRACT_HINT = 'Für diese Lehrkraft ist noch kein Mitarbeitervertrag hinterlegt – die Abrechnung ist erst danach möglich.';
export const MISSING_CONTRACT_TAG = 'kein Vertrag';
/** Error code of PayoffManagerExceptions.MissingContractException (newer backends) */
export const MISSING_CONTRACT_CODE = 'EMPLOYEE_CONTRACT_MISSING';

const TTL_MS = 2 * 60 * 1000;
const MAX_IDS = 500;

export type ContractStatusFetcher = (ids: string[], withActiveCourses: boolean) => Promise<EmployeeContractStatus[] | null>;

type Entry = { status: EmployeeContractStatus | null; at: number };

/**
 * Loader with cache (2 min) and batching: all ids asked for in the same tick go into one request (chunks of 500).
 * A fetcher result of null (no right / error) caches "unknown" for the asked ids.
 */
export function createContractStatusLoader(fetcher: ContractStatusFetcher, clock: () => number = Date.now) {
    const cache = new Map<string, Entry>();
    let queue = new Set<string>();
    let flushing: Promise<void> | null = null;
    /** Requests in flight per id (a second caller waits instead of asking again) */
    const inflight = new Map<string, Promise<void>>();

    const fresh = (id: string) => {
        const entry = cache.get(id);
        return !!entry && clock() - entry.at < TTL_MS;
    };

    async function fetchInto(ids: string[], withActiveCourses: boolean) {
        for (let i = 0; i < ids.length; i += MAX_IDS) {
            const chunk = ids.slice(i, i + MAX_IDS);
            let statuses: EmployeeContractStatus[] | null = null;
            try {
                statuses = await fetcher(chunk, withActiveCourses);
            } catch {
                statuses = null;
            }
            const by = new Map((statuses || []).map((s) => [String(s.employee), s]));
            const at = clock();
            for (const id of chunk) {
                cache.set(id, {status: by.get(id) || null, at});
            }
        }
    }

    function flush(): Promise<void> {
        if (!flushing) {
            flushing = Promise.resolve().then(async () => {
                const ids = [...queue];
                queue = new Set();
                flushing = null;
                const request = fetchInto(ids, false);
                ids.forEach((id) => inflight.set(id, request));
                try {
                    await request;
                } finally {
                    ids.forEach((id) => inflight.get(id) === request && inflight.delete(id));
                }
            });
        }
        return flushing;
    }

    function read(ids: string[]): Map<string, EmployeeContractStatus> {
        const out = new Map<string, EmployeeContractStatus>();
        for (const id of ids) {
            const status = cache.get(id)?.status;
            if (status) {
                out.set(id, status);
            }
        }
        return out;
    }

    return {
        /** Known statuses of the ids (missing = unknown) */
        async load(ids: Array<unknown>, options?: { withActiveCourses?: boolean; force?: boolean }): Promise<Map<string, EmployeeContractStatus>> {
            const wanted = Array.from(new Set(ids.map((id) => (id ? String(id) : '')).filter((id) => /^[a-f0-9]{24}$/i.test(id))));
            if (options?.withActiveCourses) {
                // profile: own request, also counts the active courses
                await fetchInto(wanted, true);
                return read(wanted);
            }
            const missing = wanted.filter((id) => options?.force || !fresh(id));
            const waits = new Set<Promise<void>>();
            for (const id of missing) {
                const running = !options?.force && inflight.get(id);
                if (running) {
                    waits.add(running);
                } else {
                    queue.add(id);
                }
            }
            if (queue.size) {
                waits.add(flush());
            }
            await Promise.all([...waits]);
            return read(wanted);
        },
        /** After creating a contract: forget the cached "no contract" */
        invalidate(id?: unknown) {
            if (id) {
                cache.delete(String(id));
            } else {
                cache.clear();
            }
        },
    };
}

/** Stable watch key of a list of ids (order-independent) */
export function contractKeyOf(ids: Array<unknown>): string {
    return Array.from(new Set(ids.filter(Boolean).map(String))).sort().join(',');
}
